import React from 'react';
import { fill, sizeStep } from '../text';
import { Text, fs } from './fields';
import { str, useSlide } from './context';

/** Title sizes for a 90-character title field: long titles step down so they stay on two or three lines. */
export const TITLE_STEPS: [number, number][] = [[26, 92], [44, 80], [64, 70], [999, 60]];

// #region useTitleSize
/** The size for a text field from its filled length (raw length in edit mode, so typing doesn't jump with bindings). */
export function useFieldSize(k: string, steps: [number, number][]): number {
    const { slide, context, mode } = useSlide();
    const raw = str(slide.fields[k]);
    return sizeStep(mode === 'edit' ? raw : fill(raw, context.values, mode), steps);
}

// #region Head
/** Eyebrow + title, the top of most slides. */
export const Head: React.FunctionComponent<{ steps?: [number, number][]; className?: string; style?: React.CSSProperties; titleStyle?: React.CSSProperties; noEyebrow?: boolean }> = ({
    steps = TITLE_STEPS, className, style, titleStyle, noEyebrow
}): React.ReactElement => {
    const size = useFieldSize('title', steps);
    return (
        <header className={`wd-head${className ? ` ${className}` : ''}`} style={style}>
            {noEyebrow ? null : <Text k="eyebrow" className="wd-eyebrow" placeholder="Eyebrow" max={40} />}
            <Text k="title" as="h2" className="wd-h" style={{ fontSize: fs(size), ...titleStyle }} placeholder="Title" max={90} />
        </header>
    );
};

// #region Logo
/** The company logo in the slide's corner. On dark slides it sits on a white tile so a dark wordmark stays legible. */
export const Logo: React.FunctionComponent<{ dark?: boolean; style?: React.CSSProperties }> = ({ dark, style }): React.ReactElement | null => {
    const { context } = useSlide();
    const url = context.theme.logo_url;
    if (!url) return null;
    return <img className={`wd-logo${dark ? ' wd-logo-tile' : ''}`} src={url} alt={context.theme.name} style={style} draggable={false} />;
};

// #region EmptyNote
/** Edit-mode only: what a computed area would show (pricing, designs), or why it's empty. */
export const EmptyNote: React.FunctionComponent<React.PropsWithChildren<{ style?: React.CSSProperties }>> = ({ style, children }): React.ReactElement | null => {
    const { edit } = useSlide();
    return edit ? <div className="wd-empty-note" style={style}>{children}</div> : null;
};
